import React, { useRef, useState, useEffect } from 'react';
import './CSS/Testimonials.css';

// Import testimonial images
import testimo1 from '../assets/images/testimonials/testimo1.png';
import testimo2 from '../assets/images/testimonials/testimo2.png';
import testimo3 from '../assets/images/testimonials/testimo3.png';
import testimo4 from '../assets/images/testimonials/testimo4.png';
import testimo5 from '../assets/images/testimonials/testimo5.png';

// Import quote icon
import quoteIcon from '../assets/icons/qoute.png';

const Testimonials = ({ background }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false); // Start with false, will be updated after mount
  
  const testimonials = [
    {
      id: 1,
      name: "James Whitmore",
      address: "Notting Hill, London",
      text: "Dropped my phone and thought it was beyond repair. fixINN had it looking brand new in less than a day. Friendly team and super quick!",
      image: testimo1
    },
    {
      id: 2,
      name: "Ayesha Patel",
      address: "Kensington",
      text: "Their computer coaching helped me feel more confident with tech. The sessions are clear, patient, and tailored to your pace.",
      image: testimo2
    },
    {
      id: 3,
      name: "Tom Reynolds",
      address: "Hammersmith",
      text: "Used their tennis stringing service — absolutely top-notch. My racket plays better than ever!",
      image: testimo3
    },
    {
      id: 4,
      name: "Sarah Jennings",
      address: "Shepherd's Bush",
      text: "I booked a home tech support session for my elderly parents. The technician was kind, professional, and explained everything simply.",
      image: testimo4
    },
    {
      id: 5,
      name: "Mark O'Neill",
      address: "Fulham",
      text: "Had my watch battery replaced and cleaned while I grabbed a coffee. Great local service, efficient and trustworthy.",
      image: testimo5
    }
  ];

  const sliderRef = useRef(null);
  let isDown = false;
  let startX;
  let scrollLeft;

  // Check scroll position and update button visibility
  const checkScrollPosition = () => {
    if (sliderRef.current) {
      const container = sliderRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      
      // Check if we're at the very beginning (first item fully visible)
      const atStart = scrollLeft === 0; // Exact check for start position
      
      // Check if we're at the very end (last item fully visible)
      const atEnd = Math.abs(scrollLeft - (scrollWidth - clientWidth)) <= 1; // Account for sub-pixel precision
      
      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    }
  };

  // Scroll left function
  const scrollLeftButton = () => {
    if (sliderRef.current) {
      const cardWidth = 370 + 24; // card width + gap
      sliderRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth'
      });
    }
  };

  // Scroll right function
  const scrollRightButton = () => {
    if (sliderRef.current) {
      const cardWidth = 370 + 24; // card width + gap
      sliderRef.current.scrollBy({
        left: cardWidth,
        behavior: 'smooth'
      });
    }
  };

  // Check scroll position on mount and when testimonials data changes
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      checkScrollPosition();
    }, 200); // Increased delay to ensure everything is rendered
    
    // Add event listener for scroll events
    const container = sliderRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Also listen for resize events to recalculate on window resize
      window.addEventListener('resize', checkScrollPosition);
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
        clearTimeout(timer);
      };
    }
    
    return () => clearTimeout(timer);
  }, []);

  const handleMouseDown = (e) => {
    isDown = true;
    sliderRef.current.classList.add('grabbing');
    startX = e.pageX - sliderRef.current.offsetLeft;
    scrollLeft = sliderRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    isDown = false;
    sliderRef.current.classList.remove('grabbing');
  };

  const handleMouseUp = () => {
    isDown = false;
    sliderRef.current.classList.remove('grabbing');
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // adjust scroll speed
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <section
      className="testimonials-section"
      style={{ backgroundColor: background || '#fff4ef' }}
    >
      <div className="container">
        <div className="testimonials-content">
          {/* Left Column */}
          <div className="testimonials-left">
            <h2 className="testimonials-title">
              Don't Just Take
              Our Word For It,<br />
              <span className="text-primary">See What Our
                Customers Say</span>
            </h2>
            <p className="testimonials-description">
              Real experiences from people who trusted fixINN — and got service they could count on.
            </p>
          </div>

          {/* Right Column */}
          <div className="testimonials-right">
            <div className="testimonials-container-wrapper">
              {/* Left scroll button */}
              {canScrollLeft && (
                <button 
                  className="scroll-button scroll-button-left" 
                  onClick={scrollLeftButton}
                  aria-label="Scroll left"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              
              {/* Right scroll button */}
              {canScrollRight && (
                <button 
                  className="scroll-button scroll-button-right" 
                  onClick={scrollRightButton}
                  aria-label="Scroll right"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              
              <div
                className="testimonials-slider"
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
              >
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="testimonial-card">
                    <img
                      src={quoteIcon}
                      alt="Quote"
                      className="testimonial-quote"
                    />
                    <p className="testimonial-text">{testimonial.text}</p>
                    <div className="testimonial-author">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="author-image"
                      />
                      <div className="author-info">
                        <h4 className="author-name">{testimonial.name}</h4>
                        <p className="author-address">{testimonial.address}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
